import React from "react";
import { useHistory } from "react-router-dom";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { queryCache, QueryKey, useMutation } from "react-query";
import groupBy from "lodash/groupBy";
import sumBy from "lodash/sumBy";

import useValidation from "fe-shared/src/hooks/useValidation";
import { transformCurrencyValue } from "fe-shared/src/helpers/transform";
import { ApiError } from "fe-shared/src/types/common";
import {
  Dialog,
  Button,
  TextInput,
  DraftFileUploader,
  showNotification,
  Loader,
  ApiErrorBanner,
  Checkbox,
  CollapsableCard,
} from "fe-shared/src/components";
import BusinessRole from "fe-shared/src/types/domain/BusinessRole";

import {
  InvoiceCreateInfo,
  InvoiceItemType,
  InvoiceTransaction,
} from "../../types";

import {
  InvoiceSummaryDetails,
  InvoicePeriodWrapper,
  UnidentifiedMaterialsBanner,
} from "..";
import BalanceAdjustmentsDetails from "../BalanceAdjustmentsDetails";
import InvoiceFeeDetails from "../InvoiceFeeDetails";
import InvoiceTransactionInfoDetails from "../InvoiceInfoDetails/InvoiceTransactionInfoDetails";
import {
  initialContent,
  getValidationConfig,
  errorMessages,
} from "./modules/validation";

import * as api from "./modules/api";

import styles from "./CreateInvoiceDialog.module.scss";

interface Props {
  isDialogActive: boolean;
  closeDialog: () => void;
  displayName: string;
  role: BusinessRole;
  invoiceInfo: InvoiceCreateInfo<InvoiceTransaction>;
  listQueryKey: QueryKey;
}

const CreateInvoiceDialog = ({
  isDialogActive,
  closeDialog,
  role,
  displayName,
  listQueryKey,
  invoiceInfo: {
    transactions,
    invoiceFees,
    paymentBalanceAdjustments,
    startDate,
    endDate,
    transactionNumbers,
    subtotal,
    invoiceTarget,
    isHSTExemptChecked,
    invoiceFeeSubTotal,
    transactionFeeSubTotal,
    paymentBalanceAdjustmentsSubTotal,
    unidentifiedMaterials,
    hstTax,
  },
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isWithTransactions = invoiceTarget === InvoiceItemType.Transaction;
  const hasInvoiceFees = !!invoiceFees?.length;
  const hasBalanceAdjustments = !!paymentBalanceAdjustments?.length;

  const transactionsByMonth = groupBy(transactions, (transaction: InvoiceTransaction) =>
    new Date(transaction.completedDate).toLocaleString("en-CA", {
      month: "long",
    })
  );
  const totalWeight = sumBy(transactions, "weight");

  const {
    errors,
    values,
    isValid,
    validateAll,
    updateValue,
    validateValue,
  } = useValidation(
    getValidationConfig(subtotal),
    initialContent,
    errorMessages
  );

  const [invoiceCreate, invoiceCreateResult] = useMutation<
    { number: string },
    AxiosError<ApiError>,
    api.CreateInvoiceParams
  >(api.createInvoice, {
    onSuccess: ({ number }) => {
      closeDialog();
      showNotification(t("transactions.invoice.successToast", { number }));
      queryCache.invalidateQueries(listQueryKey);
      history.push(`/invoices/${number}`);
    },
  });

  const onSubmit = () => {
    if (isValid) {
      invoiceCreate({
        data: values,
        transactionNumbers,
        invoiceTarget,
      });
    } else {
      validateAll();
    }
  };

  const title = (
    <div className={styles.headerWrapper}>
      <div className={styles.header}>
        <div className={styles.subtitle}>
          {`${t(`businessRoles.${role}`)}${
            displayName ? ` · ${displayName}` : ""
          }`}
        </div>
        <div className={styles.title}>
          {t("transactions.invoice.modalTitle")}
        </div>
      </div>
    </div>
  );
  const actions = [
    <Button
      key="1"
      id="create-invoice-send-btn"
      variant="filled"
      disabled={!!Object.keys(errors).length}
      onClick={onSubmit}
    >
      {t("transactions.invoice.send")}
    </Button>,
    <Button
      key="2"
      id="create-invoice-cancel-btn"
      variant="outlined"
      onClick={closeDialog}
    >
      {t("transactions.invoice.cancel")}
    </Button>,
  ];

  return (
    <Dialog
      modal
      width={648}
      isActive={isDialogActive}
      title={title}
      onClose={closeDialog}
      actions={actions}
    >
      {invoiceCreateResult.isLoading && <Loader isBlocking />}
      {invoiceCreateResult.isError && (
        <ApiErrorBanner
          id="create-invoice-error-banner"
          requestStatus={invoiceCreateResult}
        />
      )}
      {unidentifiedMaterials && (
        <UnidentifiedMaterialsBanner materials={unidentifiedMaterials} />
      )}
      <InvoicePeriodWrapper
        isWithTransactions={isWithTransactions}
        startDate={startDate}
        endDate={endDate}
        count={transactionNumbers.length}
      />
      <span className={styles.formSectionTitle}>{t("invoices.summary")}</span>
      <InvoiceSummaryDetails
        subtotal={subtotal}
        invoiceFeeSubTotal={invoiceFeeSubTotal}
        transactionFeeSubTotal={transactionFeeSubTotal}
        totalWeight={totalWeight}
        isHSTExemptChecked={isHSTExemptChecked}
        adjustmentSum={Number(values.amount)}
        isWithTransactions={isWithTransactions}
        paymentBalanceSubTotal={paymentBalanceAdjustmentsSubTotal}
        hstTax={hstTax}
      />
      <hr />
      <span className={styles.formSectionTitle}>
        {t("invoices.details.title")}
      </span>
      {(Object.entries(transactionsByMonth) as [string, InvoiceTransaction[]][]).map(
        ([month, transactionsOfMonth]) => {
          return (
            <CollapsableCard
              key={month}
              id={month}
              headerTitle={`${month} (${transactionsOfMonth.length})`}
              opened={false}
              className={styles.transactionCard}
            >
              <InvoiceTransactionInfoDetails
                transactions={transactionsOfMonth}
              />
            </CollapsableCard>
          );
        }
      )}
      <div className={styles.invoiceModificators}>
        {hasInvoiceFees && <InvoiceFeeDetails invoiceFees={invoiceFees} />}
        {hasBalanceAdjustments && (
          <BalanceAdjustmentsDetails
            balanceAdjustments={paymentBalanceAdjustments}
          />
        )}
      </div>
      <div className={styles.adjustmentData}>
        <Checkbox
          id="create-invoice-adjustment-isAdjustment"
          value={values.isAdjustment}
          title={t("invoices.create.isAdjustmentTitle")}
          onChange={(e, value) => {
            updateValue("isAdjustment", value, true);
            if (!value) {
              updateValue("amount", 0);
            }
          }}
        />
        {values.isAdjustment && (
          <>
            <span className={styles.formSectionTitle}>
              {t("transactions.invoice.adjustmentTitle")}
            </span>
            <div className={styles.sumInputWrapper}>
              <TextInput
                type="number"
                maxLength={12}
                value={values.amount}
                isError={!!errors.amount}
                errorText={errors.amount}
                id="create-invoice-adjustment-amount"
                title={t("invoices.create.sumTitle", { currency: "$" })}
                onBlur={() => validateValue("amount")}
                onChange={(e, value) => updateValue("amount", value)}
                transformValue={transformCurrencyValue}
              />
            </div>
            <div className={styles.inputReasonWrepper}>
              <TextInput
                isMultiline
                id="create-invoice-reason"
                title={t("invoices.create.reasonTitle")}
                isError={!!errors.comment}
                errorText={errors.comment}
                value={values.comment}
                onBlur={() => validateValue("comment")}
                onChange={(e, value) => updateValue("comment", value)}
              />
            </div>
          </>
        )}
      </div>
      <span className={styles.formSectionTitle}>
        {t("invoices.create.supportingDocuments")}
      </span>
      <DraftFileUploader
        files={values.attachments}
        path="invoices/attachment"
        onChange={(files) => updateValue("attachments", files, true)}
      />
    </Dialog>
  );
};

export default CreateInvoiceDialog;
